import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "September 7, 2026";

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h2 className="font-display font-semibold text-lg mb-2">{title}</h2>
      <div className="text-sm leading-relaxed text-muted-foreground space-y-2">{children}</div>
    </section>
  );
}

/** Public privacy policy — required by the Apple App Store. English only. */
export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background pt-safe">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/onsitecomms-icon.png" alt="OnSiteComms" className="h-10 w-10 rounded-lg object-contain" />
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">
              <span className="text-[#2B3990]">OnSite</span><span className="text-[#C9A03B]">Comms</span> — Privacy Policy
            </h1>
            <p className="text-xs text-muted-foreground">Effective date: {EFFECTIVE_DATE}</p>
          </div>
        </div>

        <Section title="1. Who we are">
          <p>
            OnSiteComms ("we", "us") is a private business communication platform for construction field
            teams, operated by Johnny Garbim. It is used by employees and contractors of participating
            companies to coordinate installation jobs. Website: <a className="text-[#2B3990] underline" href="https://on-site-comms.com">https://on-site-comms.com</a>.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account information:</strong> name, e-mail address, role/title and preferred language.</li>
            <li><strong>Content you create:</strong> messages, photos, videos, voice recordings, documents (e.g. job PDFs), annotations and task/issue records.</li>
            <li><strong>Job information:</strong> work orders, addresses and schedules synchronized from your company's project-management system.</li>
            <li><strong>Device information:</strong> push-notification tokens (Apple/Google) so we can deliver message alerts.</li>
            <li><strong>Sign-in credentials:</strong> a hashed password, and optionally passkeys (Face ID / Touch ID) or your Apple ID identifier if you use Sign in with Apple. We never see or store your Apple password or biometric data.</li>
          </ul>
        </Section>

        <Section title="3. How we use your information">
          <ul className="list-disc pl-5 space-y-1">
            <li>To provide the core service: real-time messaging, media sharing and job coordination.</li>
            <li>To transcribe and translate voice messages and captions (processed by OpenAI's API solely to provide this feature).</li>
            <li>To send push notifications about new messages (via Apple Push Notification service and Google Firebase Cloud Messaging).</li>
            <li>To secure accounts and prevent unauthorized access.</li>
          </ul>
          <p>We do <strong>not</strong> sell your data, show ads, or use your data for advertising or tracking across other companies' apps and websites.</p>
        </Section>

        <Section title="4. AI features (OpenAI) — your explicit choice">
          <p>
            Some features are powered by artificial intelligence provided by <strong>OpenAI</strong> (USA):
            automatic message translation, voice-note transcription, the live interpreter and the Buddy assistant.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>What is sent:</strong> the text of messages you translate, voice recordings you transcribe or speak during the live interpreter, your questions and voice commands to Buddy, and photos you explicitly ask the AI to analyze.</li>
            <li><strong>Who receives it:</strong> OpenAI, L.L.C. (United States), acting as our data processor via API. Per OpenAI's API policy, this data is <strong>not</strong> used to train their models.</li>
            <li><strong>Why:</strong> solely to provide the requested feature (translation, transcription, assistant answers). Never for advertising.</li>
            <li><strong>Your permission first:</strong> nothing is sent to OpenAI until you explicitly authorize it on the consent screen shown at your first sign-in.</li>
            <li><strong>Your choice, anytime:</strong> you can withdraw or grant this permission in <em>More → Settings → AI features</em>. If you decline, automatic translation, Buddy and audio transcription are disabled — all other features keep working.</li>
          </ul>
        </Section>

        <Section title="5. Sharing">
          <p>Your content is visible to the co-workers who participate in your conversations and to your company's administrators. We share data only with the service providers needed to run the app: cloud hosting, Apple (push), Google (push) and OpenAI (voice transcription/translation). These providers process data on our behalf and may not use it for their own purposes.</p>
        </Section>

        <Section title="6. Data retention">
          <p>We keep your data while your account is active. Messages and job records are retained as business records of the participating company. Push tokens are deleted when you sign out or delete the app.</p>
        </Section>

        <Section title="7. Deleting your account">
          <p>
            You can delete your account at any time inside the app: <strong>Profile → Delete my account</strong>.
            This permanently removes your account, sign-in credentials (password, passkeys, Apple ID link) and
            push tokens, and removes you from all conversations. Messages you previously sent may remain as
            part of your company's business records. You can also request deletion by e-mail (below).
          </p>
        </Section>

        <Section title="8. Security">
          <p>All traffic is encrypted in transit (HTTPS/TLS). Passwords are stored hashed. Access to conversations is limited to their participants and company administrators.</p>
        </Section>

        <Section title="9. Children">
          <p>OnSiteComms is a workplace tool and is not directed at children under 16.</p>
        </Section>

        <Section title="10. Changes and contact">
          <p>We may update this policy and will change the effective date above when we do.</p>
          <p>Questions or data requests: <a className="text-[#2B3990] underline" href="mailto:jlgarbim5@hotmail.com">jlgarbim5@hotmail.com</a></p>
        </Section>

        <div className="border-t pt-4 mt-8 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#2B3990] hover:underline" data-testid="privacy-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to OnSiteComms
          </Link>
          <span className="text-[11px] text-muted-foreground">Copyright © 2026 Johnny Garbim® — All rights reserved.</span>
        </div>
      </div>
    </div>
  );
}
